import React, { useRef, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { X, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import * as d3 from 'd3';
import Draggable from 'react-draggable';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);

interface MeasurementData {
  feature_label: string;
  characteristic: string;
  actual_value: number;
  deviation?: number;
  nominal_value?: number;
  upper_tolerance?: number;
  lower_tolerance?: number;
  sample_date: string;
  sample_id?: string;
  metadata?: any; // Add this line for debug logging
}

interface MeasurementPopupProps {
  markerId: string;
  featureLabel: string;
  measurements: MeasurementData[];
  nominalData?: any[];
  onClose: () => void;
  position?: { x: number; y: number };
  markerScreenPosition?: { x: number; y: number };
  containerId?: string;
}

function getSampleDateObj(sample: any) {
  const dateStr = sample.metadata?.date;
  const timeStr = sample.metadata?.time;
  const dateFormat = sample.metadata?.date_format || 'DD/MM/YYYY';
  const timeFormat = sample.metadata?.time_format || 'HH:mm:ss';

  if (dateStr && timeStr) {
    // Try parsing with the exact format first
    let result = dayjs(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`);

    // If that fails, try with more flexible parsing
    if (!result.isValid()) {
      // Try different date formats that are more flexible
      const flexibleDateFormats = [
        'DD/MM/YYYY', 'D/M/YYYY', 'DD/MM/YY', 'D/M/YY',
        'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY'
      ];
      for (const format of flexibleDateFormats) {
        result = dayjs(`${dateStr} ${timeStr}`, `${format} ${timeFormat}`);
        if (result.isValid()) break;
      }
    }
    if (result.isValid()) return result;
  }
  if (dateStr) {
    let result = dayjs(dateStr, dateFormat);
    if (!result.isValid()) {
      const flexibleDateFormats = [
        'DD/MM/YYYY', 'D/M/YYYY', 'DD/MM/YY', 'D/M/YY',
        'MM/DD/YYYY', 'M/D/YYYY', 'MM/DD/YY', 'M/D/YY'
      ];
      for (const format of flexibleDateFormats) {
        result = dayjs(dateStr, format);
        if (result.isValid()) break;
      }
    }
    if (result.isValid()) return result;
  }
  if (sample.sample_date) {
    const result = dayjs(sample.sample_date);
    if (result.isValid()) return result;
  }
  return dayjs('invalid');
}

const MeasurementPopup = ({
  markerId,
  featureLabel,
  measurements,
  nominalData = [],
  onClose,
  position = { x: 100, y: 100 },
  markerScreenPosition,
  containerId = "stl-viewer-container"
}: MeasurementPopupProps) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const [selectedCharacteristics, setSelectedCharacteristics] = useState<string[]>([]);
  const [svgLine, setSvgLine] = useState<HTMLElement | null>(null);

  // Get unique characteristics and sort them alphabetically
  const uniqueCharacteristics = Array.from(new Set(measurements.map(m => m.characteristic))).sort();

  // Auto-select first characteristic when popup opens
  useEffect(() => {
    if (uniqueCharacteristics.length > 0 && selectedCharacteristics.length === 0) {
      setSelectedCharacteristics([uniqueCharacteristics[0]]);
    }
  }, [uniqueCharacteristics.length, selectedCharacteristics.length]);

  // Create unique tooltip ID for this popup instance
  const tooltipId = `tooltip_${markerId}`;
  const lineId = `line_${markerId}`;

  // Function to update line position (like geopd-dev frameRender)
  const updateLinePosition = () => {
    if (!svgLine || !popupRef.current || !markerScreenPosition) return;

    const popupRect = popupRef.current.getBoundingClientRect();
    const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
    const containerRect = container.getBoundingClientRect();
    
    // Convert to container-relative coordinates since line is appended to container
    const markerX = markerScreenPosition.x - containerRect.left;
    const markerY = markerScreenPosition.y - containerRect.top;
    
    const popupCenterX = popupRect.left - containerRect.left + popupRect.width / 2;
    const popupCenterY = popupRect.top - containerRect.top + popupRect.height / 2;
    
    // Calculate distance and angle
    const distance = Math.sqrt(Math.pow(popupCenterX - markerX, 2) + Math.pow(popupCenterY - markerY, 2));
    const angle = Math.atan2(popupCenterY - markerY, popupCenterX - markerX) * 180 / Math.PI;
    
    // Position the line in container-relative coordinates
    svgLine.style.left = markerX + 'px';
    svgLine.style.top = markerY + 'px';
    svgLine.style.width = distance + 'px';
    svgLine.style.transform = `rotate(${angle}deg)`;
    

  };

  useEffect(() => {
    // Create CSS line element for connecting marker to popup
    const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
    
    // Check if line already exists to prevent recreation on re-renders
    let lineDiv = document.querySelector(`.${lineId}`) as HTMLDivElement;
    if (!lineDiv) {
      lineDiv = document.createElement('div');
      lineDiv.setAttribute('class', `${lineId}`);
      lineDiv.style.position = 'absolute';
      lineDiv.style.backgroundColor = '#ff0000'; // Bright red
      lineDiv.style.height = '2px'; // Original thickness
      lineDiv.style.boxShadow = '0 0 2px #ff0000'; // Original glow effect
      lineDiv.style.transformOrigin = '0 50%';
      lineDiv.style.pointerEvents = 'none';
      lineDiv.style.zIndex = '1'; // Lower z-index to appear behind popup
      lineDiv.style.opacity = '1'; // Explicitly set opacity to ensure visibility
      lineDiv.style.isolation = 'isolate'; // Create new stacking context
      lineDiv.style.overflow = 'visible'; // Ensure line is not clipped
      
      // Append to container for coordinate system alignment
      container.appendChild(lineDiv);
    }
    
    // Create tooltip element and append to parent container (like geopd-dev)
    let tooltip = document.getElementById(tooltipId) as HTMLDivElement;
    if (!tooltip) {
      tooltip = document.createElement('div');
      tooltip.setAttribute('id', tooltipId);
      tooltip.setAttribute('class', 'tooltip');
      tooltip.style.position = 'absolute';
      tooltip.style.zIndex = '10'; // Appropriate z-index for tooltip
      tooltip.style.fontFamily = "'Gill Sans', 'Gill Sans MT', Calibri, 'Trebuchet MS', sans-serif";
      tooltip.style.fontSize = 'small';
      tooltip.style.backgroundColor = 'white';
      tooltip.style.border = '1px solid #ccc';
      tooltip.style.padding = '8px';
      tooltip.style.borderRadius = '4px';
      tooltip.style.pointerEvents = 'none';
      tooltip.style.transition = 'opacity 0.15s ease-in-out';
      tooltip.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
      tooltip.style.opacity = '0';
      
      container.appendChild(tooltip);
    }
    
    setSvgLine(lineDiv);
    
    // Update position after popup is rendered
    const timeoutId = setTimeout(updateLinePosition, 50);

    return () => {
      clearTimeout(timeoutId);
      // Remove line and tooltip when component unmounts
      if (lineDiv && lineDiv.parentNode) {
        lineDiv.parentNode.removeChild(lineDiv);
      }
      if (tooltip && tooltip.parentNode) {
        tooltip.parentNode.removeChild(tooltip);
      }
    };
  }, [markerScreenPosition, containerId, lineId, tooltipId]);

  useEffect(() => {
    // Update line position when popup moves or line reference changes
    updateLinePosition();
  }, [popupRef, svgLine, markerScreenPosition, updateLinePosition]);

  const handleCharacteristicChange = (characteristic: string, checked: boolean) => {
    if (checked) {
      setSelectedCharacteristics(prev => [...prev, characteristic]);
    } else {
      setSelectedCharacteristics(prev => prev.filter(c => c !== characteristic));
    }
  };

  const renderGraph = (characteristic: string, index: number) => {
    const characteristicData = measurements.filter(m => m.characteristic === characteristic);
    
    if (characteristicData.length === 0) return null;

    // Get nominal information from the nominal_information table
    const nominalInfo = nominalData.find(n => 
      n.feature_label === featureLabel && n.characteristic === characteristic
    );

    // Get tolerance values from nominal_information table
    const upperTol = nominalInfo?.upper_tolerance;
    const lowerTol = nominalInfo?.lower_tolerance;

    // Calculate deviations using nominal values from database
    const getDeviation = (d: any) => {
      if (d.deviation !== undefined) {
        return d.deviation;
      } else if (d.actual_value !== undefined && nominalInfo?.nominal !== undefined) {
        return d.actual_value - nominalInfo.nominal;
      } else {
        return d.actual_value || 0;
      }
    };

    const sortedCharacteristicData = [...characteristicData].sort((a, b) => {
      const dateA = getSampleDateObj(a);
      const dateB = getSampleDateObj(b);
      if (!dateA.isValid() && !dateB.isValid()) return 0;
      if (!dateA.isValid()) return 1;
      if (!dateB.isValid()) return -1;
      return dateA.valueOf() - dateB.valueOf(); // oldest to newest (left to right)
    });
    const chartData = sortedCharacteristicData.map((sample, i) => {
      const deviation = getDeviation(sample);
      const isInTolerance = upperTol !== undefined && lowerTol !== undefined 
        ? deviation >= lowerTol && deviation <= upperTol 
        : true;
      return {
        x: i + 1, // Use index + 1 for x-axis
        y: deviation,
        color: isInTolerance ? "green" : "red",
        sample: sample.sample_id || `Sample ${i + 1}`,
        characteristic: characteristic,
        deviation: deviation
      };
    });

    // Use chartData.map(d => d.y) for stats
    const validDeviations = chartData.map(d => d.y).filter(d => !isNaN(d) && d !== undefined);
    if (validDeviations.length === 0) {
      return (
        <div key={characteristic} className="mb-4 p-3 border rounded">
          <h4 className="font-semibold text-sm">{characteristic}</h4>
          <div className="text-center text-gray-500 text-sm py-4">
            No valid measurement data available
          </div>
        </div>
      );
    }

    // Calculate statistics using valid deviations
    const mean = validDeviations.reduce((a, b) => a + b, 0) / validDeviations.length;
    const stdDev = Math.sqrt(validDeviations.map(x => Math.pow(x - mean, 2)).reduce((a, b) => a + b) / validDeviations.length);
    
    // Calculate Cp and Cpk only if we have tolerance values
    let cp = 0;
    let cpk = 0;
    
    if (upperTol !== undefined && lowerTol !== undefined && stdDev > 0) {
      cp = (upperTol - lowerTol) / (6 * stdDev);
      cpk = Math.min(
        (upperTol - mean) / (3 * stdDev),
        (mean - lowerTol) / (3 * stdDev)
      );
      

    }

    const getCpColor = (value: number) => {
      if (value < 1) return 'bg-red-500';  // This now includes negative values and 0-1 range
      if (value <= 1.33) return 'bg-orange-500';
      return 'bg-green-500';
    };

    // Create chart data structure like geopd-dev
    // Create tolerance line data
    const upperTolData = upperTol !== undefined ? chartData.map(d => ({ x: d.x, y: upperTol })) : [];
    const lowerTolData = lowerTol !== undefined ? chartData.map(d => ({ x: d.x, y: lowerTol })) : [];

    // Chart dimensions matching geopd-dev
    const chartWidth = 250;
    const chartHeight = 150;
    const margin = { top: 20, right: 15, bottom: 40, left: 25 };
    const innerWidth = chartWidth - margin.left - margin.right;
    const innerHeight = chartHeight - margin.top - margin.bottom;

    // Calculate scale like geopd-dev
    const valueAbs = validDeviations.map(v => Math.abs(v));
    const tolBand = upperTol !== undefined && lowerTol !== undefined 
      ? [Math.abs(upperTol), Math.abs(lowerTol)]
      : [];
    
    const maxTol = tolBand.length > 0 ? Math.max(...tolBand) : 0;
    const maxVal = Math.max(...valueAbs);
    const yScale = maxTol >= maxVal ? maxTol : maxVal;

    return (
      <div key={characteristic} className="mb-4 p-3 border rounded">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-semibold text-sm">{characteristic}</h4>
          <div className="flex gap-2">
            {upperTol !== undefined && lowerTol !== undefined && stdDev > 0 && (
              <Badge className={`${getCpColor(cp)} text-white`}>
                Cp: {cp.toFixed(2)}
              </Badge>
            )}
            {upperTol !== undefined && lowerTol !== undefined && stdDev > 0 && (
              <Badge className={`${getCpColor(cpk)} text-white`}>
                Cpk: {cpk.toFixed(2)}
              </Badge>
            )}
          </div>
        </div>
        
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div>Mean: {mean.toFixed(3)}</div>
          <div>StdDev: {stdDev.toFixed(3)}</div>
          <div>Min: {Math.min(...validDeviations).toFixed(3)}</div>
          <div>Max: {Math.max(...validDeviations).toFixed(3)}</div>
        </div>

        {/* SVG Line Chart matching geopd-dev */}
        <div className="mt-2 relative">
          <div className="text-xs text-gray-600 mb-1">
            Deviation Trend ({validDeviations.length} samples)
          </div>
          
          <svg width={chartWidth} height={chartHeight} className="border border-gray-300 bg-white">
            {/* Chart area background */}
            <rect
              x={margin.left}
              y={margin.top}
              width={innerWidth}
              height={innerHeight}
              fill="none"
              stroke="black"
              strokeWidth="0.5"
            />
            
            {/* Create scales using D3 logic like geopd-dev */}
            <g>
              {/* X-axis */}
              {chartData.map((d, i) => (
                <g key={`x-axis-${i}`}>
                  <line
                    x1={margin.left + (i / (chartData.length - 1)) * innerWidth}
                    y1={margin.top + innerHeight}
                    x2={margin.left + (i / (chartData.length - 1)) * innerWidth}
                    y2={margin.top + innerHeight + 5}
                    stroke="black"
                    strokeWidth="0.5"
                  />
                  <text
                    x={margin.left + (i / (chartData.length - 1)) * innerWidth}
                    y={margin.top + innerHeight + 15}
                    textAnchor="middle"
                    fontSize="8"
                    fill="black"
                  >
                    {d.x}
                  </text>
                </g>
              ))}
              
              {/* Y-axis ticks */}
              {[-1.5 * yScale, -yScale, -0.5 * yScale, 0, 0.5 * yScale, yScale, 1.5 * yScale].map((tickValue, i) => {
                const yPos = margin.top + innerHeight - ((tickValue + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                return (
                  <g key={`y-axis-${i}`}>
                    <line
                      x1={margin.left - 5}
                      y1={yPos}
                      x2={margin.left}
                      y2={yPos}
                      stroke="black"
                      strokeWidth="0.5"
                    />
                    <text
                      x={margin.left - 8}
                      y={yPos + 2}
                      textAnchor="end"
                      fontSize="8"
                      fill="black"
                    >
                      {tickValue.toFixed(1)}
                    </text>
                  </g>
                );
              })}
              
              {/* Upper tolerance line */}
              {upperTolData.length > 0 && (
                <path
                  d={upperTolData.map((d, i) => {
                    const x = margin.left + (i / (chartData.length - 1)) * innerWidth;
                    const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="red"
                  strokeWidth="1"
                />
              )}
              
              {/* Lower tolerance line */}
              {lowerTolData.length > 0 && (
                <path
                  d={lowerTolData.map((d, i) => {
                    const x = margin.left + (i / (chartData.length - 1)) * innerWidth;
                    const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                  }).join(' ')}
                  fill="none"
                  stroke="red"
                  strokeWidth="1"
                />
              )}
              
              {/* Data line */}
              <path
                d={chartData.map((d, i) => {
                  const x = margin.left + (i / (chartData.length - 1)) * innerWidth;
                  const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                  return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                }).join(' ')}
                fill="none"
                stroke="blue"
                strokeWidth="1"
              />
              
              {/* Data points with tooltips */}
              {chartData.map((d, i) => {
                const x = margin.left + (i / (chartData.length - 1)) * innerWidth;
                const y = margin.top + innerHeight - ((d.y + 1.5 * yScale) / (3 * yScale)) * innerHeight;
                
                return (
                  <circle
                    key={i}
                    cx={x}
                    cy={y}
                    r="3"
                    fill={d.color}
                    stroke="none"
                    strokeWidth="1"
                    style={{ cursor: 'pointer' }}
                    onMouseOver={(e) => {
                      const tooltip = document.getElementById(tooltipId);
                      if (tooltip) {
                        tooltip.innerHTML = `Sample: ${d.sample}<br/>Characteristic: ${d.characteristic}<br/>Deviation: ${d.deviation.toFixed(3)}`;
                        tooltip.style.opacity = '1';
                        
                        // Position tooltip relative to container using transform (like geopd-dev)
                        const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
                        const containerRect = container.getBoundingClientRect();
                        const mouseX = e.clientX - containerRect.left;
                        const mouseY = e.clientY - containerRect.top;
                        
                        tooltip.style.left = mouseX + 'px';
                        tooltip.style.top = mouseY + 'px';
                        tooltip.style.transform = 'translate(10px, 15px)'; // Slightly to the right and below cursor
                      }
                    }}
                    onMouseMove={(e) => {
                      const tooltip = document.getElementById(tooltipId);
                      if (tooltip) {
                        // Update tooltip position relative to container using transform
                        const container = document.getElementById(containerId) || document.querySelector('[role="dialog"]') || document.body;
                        const containerRect = container.getBoundingClientRect();
                        const mouseX = e.clientX - containerRect.left;
                        const mouseY = e.clientY - containerRect.top;
                        
                        tooltip.style.left = mouseX + 'px';
                        tooltip.style.top = mouseY + 'px';
                        tooltip.style.transform = 'translate(10px, 15px)'; // Slightly to the right and below cursor
                      }
                    }}
                    onMouseLeave={() => {
                      const tooltip = document.getElementById(tooltipId);
                      if (tooltip) {
                        tooltip.style.opacity = '0';
                      }
                    }}
                  />
                );
              })}
            </g>
          </svg>
        </div>
      </div>
    );
  };

  return (
    <Draggable
      nodeRef={popupRef}
      axis="both"
      defaultPosition={{ x: position.x, y: position.y }}
      onDrag={(e) => updateLinePosition()}
      bounds={`#${containerId}`}
    >
      <div
        ref={popupRef}
        className="absolute bg-white border shadow-lg rounded-lg w-80 max-h-96 overflow-y-auto"
        style={{
          cursor: 'grab',
          zIndex: 10  // Match geopd-dev container z-index
        }}
      >
        <div className="p-3 relative">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold">{featureLabel}</h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Characteristic Selection */}
          <div className="mb-4">
            <h4 className="text-sm font-medium mb-2">Select Characteristics:</h4>
            <div className="space-y-2">
              {uniqueCharacteristics.map((characteristic) => (
                <div key={characteristic} className="flex items-center space-x-2">
                  <Checkbox
                    id={`${markerId}_${characteristic}`}
                    checked={selectedCharacteristics.includes(characteristic)}
                    onCheckedChange={(checked) => 
                      handleCharacteristicChange(characteristic, checked as boolean)
                    }
                  />
                  <label 
                    htmlFor={`${markerId}_${characteristic}`}
                    className="text-sm cursor-pointer"
                  >
                    {characteristic}
                  </label>
                  <Badge variant="outline" className="text-xs">
                    {measurements.filter(m => m.characteristic === characteristic).length}
                  </Badge>
                </div>
              ))}
            </div>
            
          </div>

          {/* Graphs for selected characteristics */}
          <div className="space-y-4 overflow-visible">
            {selectedCharacteristics.map((characteristic, index) => 
              renderGraph(characteristic, index)
            )}
          </div>

          {selectedCharacteristics.length === 0 && (
            <div className="text-center text-gray-500 text-sm py-4">
              Select characteristics to view measurement data
            </div>
          )}
        </div>
      </div>
    </Draggable>
  );
};

export default MeasurementPopup; 